import type { AxiosError } from 'axios';
import axios from 'axios';

export * from './serialManagement';
export * from './storeValueCard';
export * from './survey';
export * from './brandTabs';

export interface Response<T> {
  error?: AxiosError<{
    data: {
      message?: string;
      error_code?: string;
      data?: {
        message?: string;
        error_code?: string;
        getting_start_date?: string;
        getting_end_date?: string;
      };
      errors?: { [key: string]: string };
    } & T;
  }>;
  data?: T;
  message?: string;
  meta?: {
    current_page: number;
    per_page: number;
    total: number;
  };
}

export const echossAxios = axios.create({
  baseURL:
    process.env.REACT_APP_ENV === 'prod'
      ? 'https://api.echoss.vip'
      : process.env.REACT_APP_ENV === 'stage'
      ? 'https://stage-api.echoss.vip'
      : undefined,
});
echossAxios.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (!axios.isAxiosError(error)) {
      throw error;
    }
    if (error.config?.url === '/api/front_logging') {
      throw error;
    }

    const responseUrl: unknown = error.request?.responseURL;
    const requestUrl =
      typeof responseUrl === 'string' && responseUrl
        ? responseUrl
        : error.config?.url;
    if (typeof requestUrl !== 'string' || !requestUrl) {
      throw error;
    }

    if (
      requestUrl.indexOf('/api/member/brands/') === -1 &&
      error.response?.status === 503
    ) {
      const parsedData = window.location.pathname.split('/');
      const brandIdentify = parsedData[1];
      window.location.href = `/${brandIdentify}`;
    }
    if (requestUrl.indexOf('/api/member/login') === -1) {
      if (
        requestUrl.indexOf('api/member/commodity-transfer-get') !== -1 ||
        requestUrl.indexOf('api/member/transfer') !== -1 ||
        requestUrl.indexOf('api/member/coupon-url') !== -1 ||
        requestUrl.indexOf('api/member/tf') !== -1 ||
        requestUrl.indexOf('api/member/exchange-options') !== -1 ||
        requestUrl.indexOf('api/member/mps-cards/exchange-coupons') !== -1
      ) {
        const errorMessage = error.response?.data?.data?.message;
        const errorCode =
          error.response?.data?.error_code || error.response?.data?.data?.error_code;

        if (
          errorCode === 'E0009' ||
          errorCode === 'E0010' ||
          errorCode === 'E0011' ||
          errorCode === 'E0012' ||
          errorCode === 'E0013' ||
          errorCode === 'E0014' ||
          errorCode === 'E0015' ||
          errorCode === 'E0016' ||
          errorCode === 'E0017' ||
          errorCode === 'E0024' ||
          errorMessage === 'transfer over' ||
          errorMessage === 'over coupon event allow limit' ||
          errorMessage === 'coupon already in ticket folder' ||
          errorMessage === 'commodity already in ticket folder' ||
          errorMessage === 'user had got this coupon from url before' ||
          errorMessage === 'commodity transfer over 24hrs' ||
          errorMessage === 'transfer cancelled' ||
          errorMessage === 'this coupon during the inactive period' ||
          errorMessage === 'coupon has expired' ||
          errorMessage === 'commodity has expired' ||
          errorMessage === 'count for coupon is over the limit.' ||
          errorMessage === 'coupon info id error'
        ) {
          throw error;
        }
      }
    }
    throw error;
  },
);
interface LoginType {
  data: {
    access_token: string;
    expire_in: number;
    token_type: string;
    // 一般用戶 1 正式用戶 0
    is_common: 0 | 1;
  };
}
/**
 * 會員登入
 * @param lineToken line ID Token
 * @param brandID 品牌ID
 */
export const Login = async (
  lineToken: string,
  brandID: number,
  autoRegister?: boolean,
  phone_number?: string,
): Promise<Response<LoginType>> => {
  try {
    const { data } = await echossAxios.request<LoginType>({
      url: '/api/member/login',
      method: 'POST',
      data: {
        brand_id: brandID,
        by_line_id: autoRegister,
      },
      headers: {
        Authorization: `Bearer ${lineToken}`,
      },
    });
    return { data };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};

/**
 * GodMode會員登入
 * @param brand_id 品牌ID
 * @param gm_token gm_token
 * @param param phone number
 */
export const GodModeLogin = async (
  brandID: number,
  gm_token: string,
  param: string,
): Promise<Response<LoginType>> => {
  try {
    const { data } = await echossAxios.request<LoginType>({
      url: '/api/member/login',
      method: 'POST',
      data: {
        brand_id: brandID,
        gm_token: gm_token,
        param: param,
      },
      // headers: {
      //   Authorization: `Bearer ${lineToken}`,
      // },
    });
    return { data };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};

export interface BrandInfo {
  id: number;
  liff_id: string;
  name: string;
  user_agreement: string;
  privacy_policy: string;
  brand_code: string;
  identifier: string;
  member_benifit: string;
  language: string;
  required_registration_toggles: {
    user_email: number;
    user_gender: number;
    user_city: number;
    user_city_input_type: string;
    user_job: number;
    user_edu: number;
    user_mrg: number;
    user_hbt: number;
    user_diet: number;
    user_birth: number;
    user_name: number;
    user_invoice_carrier: number;
  };
  brand_modules: {
    // 集點卡
    loyalty_card: 1 | 0;
    // 會員卡
    member_card: 1 | 0;
    // 入會禮
    welcome_gift: 1 | 0;
    // 生日禮
    birth_gift: 1 | 0;
    // 優惠券
    coupon: 1 | 0;
    // 商店
    store: 1 | 0;
    // 外部活動
    exteranl_activity: 1 | 0;
    // 核銷編號
    show_deduct_no: 1 | 0;
    // 票券核銷方式 1印章 2條碼 3印章+條碼 4代碼 5印章+代碼 6條碼+代碼 7並存
    type_deduct: 1 | 2 | 3 | 4 | 5 | 6 | 7;
    // 點數使用方式 1印章 2條碼
    multiple_deduct: 0 | 1;
    single_deduct: 0 | 1;
    type_points: 1 | 2;
    stamp_sound: 0 | 1;
    otp: 1 | 0;
    member_referral: 1 | 0;
    serial_management: 1 | 0;
  };
  menu: { id: string; title: string }[];
  tabs?: import('./brandTabs').BrandTabConfigItem[];
  tab_config_version?: string | null;
  menu_color: string;
  line_oa?: string;
  // 會員卡是否啟用
  member_card_available: 0 | 1;
  // 會員集點卡是否啟用
  mps_card_available: 0 | 1;
  // 會員卡新增消費金額是否啟用
  add_amount_available: 0 | 1;
  // 會員集點卡集點是否啟用
  add_mps_available: 0 | 1;
  // 會員集點卡扣點是否啟用
  deduct_mps_available: 0 | 1;
  // 會員集點卡轉贈點數是否啟用
  transfer_mps_available: 0 | 1;
  // 自動轉券/自由選券是否啟用
  mps_to_coupon_available: 0 | 1;
  // 是否界接肚肚POS
  with_dudoo: 0 | 1;
  // 會員卡顯示方式：1印章 2條碼 3印章+條碼
  member_card_display: 1 | 2 | 3;
  balance_available: 0 | 1;
}
interface BrandInfoRes {
  data: BrandInfo;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Validate the fields required to start this H5, rather than every optional brand setting.
const hasBrandStartupData = (brand: unknown): boolean =>
  isRecord(brand) &&
  typeof brand.id === 'number' &&
  Number.isSafeInteger(brand.id) &&
  brand.id > 0 &&
  typeof brand.identifier === 'string' &&
  brand.identifier.trim().length > 0 &&
  typeof brand.liff_id === 'string' &&
  brand.liff_id.trim().length > 0 &&
  isRecord(brand.brand_modules) &&
  isRecord(brand.required_registration_toggles) &&
  Array.isArray(brand.menu);

export interface BrandLoadDiagnostic {
  kind:
    | 'network_error'
    | 'http_error'
    | 'invalid_response'
    | 'client_error'
    | 'missing_identifier';
  status?: number;
  code?: string;
}
/**
 * 獲取品牌相關設定
 * - liff id
 * - menu order
 * - main olor
 * @param brandID 品牌ID
 */
export const getBrandInfo = async (
  brandID: string,
): Promise<Response<BrandInfo> & { diagnostic?: BrandLoadDiagnostic }> => {
  let responseStatus: number | undefined;
  try {
    const { data, status } = await echossAxios.request<BrandInfoRes>({
      url: `/api/member/brands/${brandID}`,
    });
    responseStatus = status;
    if (!isRecord(data) || !hasBrandStartupData(data.data)) {
      return {
        data: undefined,
        diagnostic: { kind: 'invalid_response', status },
      };
    }
    return { data: data.data };
  } catch (error: any) {
    console.log(error);
    const diagnostic: BrandLoadDiagnostic =
      responseStatus !== undefined
        ? { kind: 'invalid_response', status: responseStatus }
        : axios.isAxiosError(error)
          ? {
              kind: error.response ? 'http_error' : 'network_error',
              status: error.response?.status,
              code: error.code,
            }
          : { kind: 'client_error' };
    return { error, data: undefined, diagnostic };
  }
};

interface MemberRegister {
  data: {
    has_reward_record: boolean;
    message: string;
  };
}
type RegisterFormData = {
  user_email?: string | null;
  user_phone?: string | null;
  user_phone_confirmation?: string | null;
  user_name?: string | null;
  user_city?: string | null;
  user_gender?: string | null;
  user_job?: string | null;
  user_edu?: string | null;
  user_mrg?: string | null;
  user_hbt?: string | null;
  user_diet?: string | null;
  user_birth?: string | null;
  user_invoice_carrier?: string | null;
  answers?: { id: string; item_id?: string | number | number[]; answer?: string | null }[];
};
interface RegisterResponse<T> {
  error?: AxiosError<{
    message: string;
    error_code?: string;
    data?: {
      message?: string;
      error_code?: string;
    };
    errors: { [key: string]: string };
  }>;
  data?: T;
}
/**
 * 會員註冊
 * @param lineToken line token
 * @param brandID 品牌 ID
 * @param formData 註冊資料
 */
export const postRegister = async (
  lineToken: string,
  brandID: number,
  formData: RegisterFormData,
): Promise<RegisterResponse<MemberRegister>> => {
  try {
    const { data } = await echossAxios.request<MemberRegister>({
      url: '/api/member/register',
      method: 'POST',
      data: {
        ...formData,
        brand_id: `${brandID}`,
      },
      headers: {
        Authorization: `Bearer ${lineToken}`,
      },
    });

    return { data: data };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};
export type TagFormData = {
  tag: string;
  line_id: string;
  brand_id: string;
};
/**
 * 貼標籤 API
 * @param formData TagFormData
 * @returns
 */
export const tagUser = async (
  formData: TagFormData,
): Promise<Response<{ data: { message: string } }>> => {
  try {
    const { data } = await echossAxios.request<{ data: { message: string } }>({
      url: '/api/tag-user-by-liff',
      method: 'POST',
      data: {
        ...formData,
      },
    });

    return { data: data };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};

/**
 * OTP Send
 * @param formData OTPFormData
 * @returns
 * @api https://www.notion.so/OTP-API-ad345e67bb62497886ac5d9ca4215aaa
 */
export type OTPFormData = {
  // R: 註冊 M: 修改
  type: 'R' | 'M';
  user_phone: string;
  brand_id: number;
};
export const requestOtp = async (
  formData: OTPFormData,
): Promise<Response<{ data: { message: string } }>> => {
  try {
    const { data } = await echossAxios.request<{ data: { message: string } }>({
      url: '/api/member/otp/send-auth-code',
      method: 'POST',
      data: {
        ...formData,
      },
    });

    return { data: data };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};

/**
 * Manual OTP Validation
 * @param formData ManualOTPFormData
 * @returns
 * @api 508
 */
export type ManualOTPFormData = {
  user_phone: string;
  auth_code: string;
  brand_id: string;
};
export const manualOtpValidate = async (
  formData: ManualOTPFormData,
): Promise<Response<{ data: { message: string } }>> => {
  try {
    const { data } = await echossAxios.request<{ data: { message: string } }>({
      url: '/api/member/date-otp/authentication',
      method: 'POST',
      data: {
        ...formData,
      },
    });

    return { data: data };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};

/**
 * 獲取擴充欄位
 * @returns
 * @apinums 505
 */
export type ExtendField = {
  id: number;
  required: 0 | 1;
  name: string;
  items?: { id: number; name: string }[];
  // 1: 單選題 2:問答題 3:多選題
  type: 1 | 2 | 3;
  // 2: 選擇日期 3: 輸入整數值 4: 輸入字串
  answer_type: 1 | 2 | 3 | 4;
};
export const queryExtendFields = async (
  brand_id: number,
): Promise<Response<{ data: ExtendField[] }>> => {
  try {
    const { data } = await echossAxios.request<{ data: ExtendField[] }>({
      url: '/api/member/user-extends',
      params: {
        brand_id,
      },
      method: 'GET',
    });

    return { data: data };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};

export interface ExistField {
  id: number;
  name: string;
  email: null;
  email_verified_at: null;
  created_at: string;
  updated_at: string;
  line_id: null;
  brand_id: number;
  phone_number: string;
  gender: string;
  occupation: string;
  location: string;
  education_level: string;
  marriage_status: string;
  interest: string;
  dietary_preference: string;
  birthday: string;
  invoice_carrier: null;
  member_card_id: null;
  get_mc_reason: null;
  get_mc_source: null;
  total_amount: null;
  period_amount: null;
  gift_time: null;
  common: number;
  disconnect_line: number;
  uid: string;
  source_tag_id: number;
  member_card_updated_at: string;
  should_member_card_be_renewed: boolean;
  should_member_card_renew_period_be_used: boolean;
  mps_card_points: number;
  pre_line_id: string;
  age: number;
  age_name: string;
  join_date: string;
  member_card_due_at: null;
  rich_menu_updated_at: null;
  rich_menu_alias_id: null;
  freeze_coupon_id: null;
}
/**
 * OTP驗證獲取匯入的會員資料
 * @returns
 * @apinums 507
 */
export const queryExistFieldsWithDateOTPAuth = async (params: {
  auth_code: string;
  brand_id: string;
  user_phone?: string;
  line_id?: string;
}): Promise<Response<ExistField>> => {
  try {
    const { data } = await echossAxios.request<{ data: { message: string; user: ExistField } }>({
      url: '/api/member/date-otp/authentication-user-fields',
      data: {
        ...params,
      },
      method: 'POST',
    });

    return { data: data.data.user, message: data.data.message };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};
/**
 * OTP驗證獲取匯入的會員資料
 * @returns
 * @apinums 507
 */
export const queryExistFieldsWithOTPAuth = async (params: {
  auth_code: string;
  brand_id: string;
  user_phone?: string;
  line_id?: string;
}): Promise<Response<ExistField>> => {
  try {
    const { data } = await echossAxios.request<{ data: { message: string; user: ExistField } }>({
      url: '/api/member/otp/authentication-user-fields',
      data: {
        ...params,
      },
      method: 'POST',
    });

    return { data: data.data.user, message: data.data.message };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};

export interface Widget {
  id: number;
  brand_id: number;
  name: string;
  icon: string;
  background_color: string;
  target_url: string;
}

export const queryWidgets = async (token: string): Promise<Response<Widget[]>> => {
  try {
    const { data } = await echossAxios.request<{ data: Widget[] }>({
      url: '/api/member/widgets',
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return { data: data.data };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};

export const queryCountries = async () => {
  return echossAxios.request<{ data: Record<string, string> }>({
    url: '/api/country-list',
    method: 'GET',
  });
};
